import React, { useState } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table";
import { MdDeleteForever } from "react-icons/md";
import styled from "styled-components";

// estilos para la tabla
const ContenedorTabla = styled.div`
  max-height: calc(100vh - 100px);
  overflow-x: auto;
  overflow-y: auto;
`;

const Tabla = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 14px;
  text-align: left;
`;

const HeaderTh = styled.th`
  background-color: #f2f2f2;
  color: #333;
  padding: 8px;
  border: 1px solid #ddd;
  cursor: pointer;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BodyTr = styled.tr`
  border-bottom: 1px solid #ddd;
  &:nth-child(even) {
    background-color: #f9f9f9;
    color: black;
  }
`;

const TableTd = styled.td`
  padding: 8px;
  border: 1px solid #ddd;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteTd = styled.td.attrs(({ isModalOpen }) => ({
  // Filtrado de props personalizadas
}))`
  position: sticky;
  right: 0;
  background-color: #d3d3d3;
  z-index: 1;
  padding: 8px;
  border: 1px solid #ddd;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: ${({ $isModalOpen }) => ($isModalOpen ? "none" : "table-cell")};
`;

const PaginationButton = styled.button`
  margin: 5px;
  padding: 5px 10px;
  background-color: #c20000;
  color: white;
  border: none;
  cursor: pointer;
  &:disabled {
    background-color: #d3d3d3;
    cursor: not-allowed;
  }
`;

const SearchInput = styled.input`
  padding: 4px;
  margin: auto;
  width: 50%;
  box-sizing: border-box;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  color: black;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  width: 300px;
`;

const ModalButton = styled.button`
  padding: 10px;
  margin: 10px;
  background-color: #c20000;
  color: white;
  border: none;
  cursor: pointer;
`;

function FirestoreTable({ columns, data, deleteRow }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [filterInput, setFilterInput] = useState("");

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      globalFilter: (rows, columnIds, filterValue) => {
        if (!filterValue) return rows;
        return rows.filter((row) =>
          columnIds.some((columnId) => {
            const cellValue = row.values[columnId];
            return (
              cellValue &&
              String(cellValue)
                .toLowerCase()
                .includes(filterValue.toLowerCase())
            );
          })
        );
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleDelete = (docId) => {
    setRowToDelete(docId);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    deleteRow(rowToDelete);
    setIsModalOpen(false);
    setRowToDelete(null);
  };

  const cancelDelete = () => {
    setIsModalOpen(false);
    setRowToDelete(null);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value || "";
    setFilterInput(value);
    setGlobalFilter(value);
  };

  return (
    <>
      <SearchInput
        value={filterInput}
        onChange={handleFilterChange}
        placeholder="Buscar..."
      />
      <ContenedorTabla>
        <Tabla {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                key={`header-${headerGroup.id}`}
              >
                {headerGroup.headers.map((column) => (
                  <HeaderTh
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                  >
                    {column.render("Header")}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </HeaderTh>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <BodyTr
                  {...row.getRowProps()}
                  key={`row-${row.original.docId || row.index}`}
                >
                  {row.cells.map((cell) => (
                    <TableTd {...cell.getCellProps()} key={cell.column.id}>
                      {cell.render("Cell")}
                    </TableTd>
                  ))}
                  <DeleteTd $isModalOpen={isModalOpen}>
                    <MdDeleteForever
                      size={20}
                      style={{ cursor: "pointer", color: "#c20000" }}
                      onClick={() => handleDelete(row.original.docId)}
                    />
                  </DeleteTd>
                </BodyTr>
              );
            })}
          </tbody>
        </Tabla>
      </ContenedorTabla>
      <div>
        <span>
          Página {pageIndex + 1} de {pageOptions.length}
        </span>
        <PaginationButton onClick={previousPage} disabled={!canPreviousPage}>
          Anterior
        </PaginationButton>
        <PaginationButton onClick={nextPage} disabled={!canNextPage}>
          Siguiente
        </PaginationButton>
      </div>

      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <p>¿Estás seguro de que deseas eliminar esta fila?</p>
            <ModalButton onClick={confirmDelete}>Confirmar</ModalButton>
            <ModalButton onClick={cancelDelete}>Cancelar</ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

export default FirestoreTable;
